/**
 * How an imported dish's ingredients land on the dish being edited (Item Mapping › Import).
 *
 * Moved out of itemmap.js's runRepeatImport VERBATIM so it can be tested — the component is
 * too large to mount — plus one rule that is new:
 *
 *   A SAVED PREPARATION ALREADY ON THE DISH IS ADDED ONTO.
 *
 *   On a first import, an ingredient the dish already has keeps its quantity ("an import
 *   never rewrites what you typed"). A saved preparation is the exception: when the dish
 *   has Garam Masala (made from "Garam Masala Indian") 20 g and the imported dish also uses
 *   it at 20 g, the dish now needs 40 g of it. The preparation's own ingredients need no
 *   separate handling — they are never rows on the dish. A sale deducts each of them as
 *   component weight × (row quantity ÷ batch weight) (backend/utils/ingredientDeduction.js),
 *   and the save summary scales them the same way, so they follow the row by construction.
 *
 * Everything else behaves exactly as before, and the tests pin both halves.
 */

import { convert } from './unitDisplay';

export const normalizeImportName = (value) => String(value || '').trim().toLowerCase();

/**
 * `addPerPlate` (in `srcUnit`) added onto one ingredient row, in the row's own unit.
 * Rows hold 10-plate and 100-plate quantities; per plate is qty10/10, else qty100/100.
 *
 * @returns the updated row, or null when the two units have no honest conversion
 *          (g onto an ml row) — the caller leaves that row alone and reports it.
 */
export function addPerPlateOntoRow(row, srcUnit, addPerPlate) {
  const rowUnit = row.unit || srcUnit;
  let addInRowUnit = addPerPlate;
  if (srcUnit && rowUnit && srcUnit !== rowUnit) {
    const converted = convert(addPerPlate, srcUnit, rowUnit);
    if (converted === null) return null;
    addInRowUnit = converted;
  }

  const q10 = Number(row.qty10 || 0);
  const q100 = Number(row.qty100 || 0);
  const existingPerPlate = q10 > 0 ? q10 / 10 : (q100 > 0 ? q100 / 100 : 0);
  const nextPerPlate = existingPerPlate + addInRowUnit;
  return {
    ...row,
    unit: rowUnit,
    qty10: Number((nextPerPlate * 10).toFixed(4)),
    qty100: Number((nextPerPlate * 100).toFixed(4)),
  };
}

/**
 * The plates import ("How many plates?").
 *
 * @param {object}   args
 * @param {Array}    args.baseList            the dish's rows (placeholder row already dropped)
 * @param {Array}    args.rows                the source's rows, grouped by name (qty10 = 10 plates)
 * @param {number}   args.plates              how many plates of the source to add
 * @param {boolean}  args.first               true when this source is not on the dish yet
 * @param {Function} args.isSavedPreparation  (name) => whether that ingredient is a saved
 *                                            preparation, resolved the way the screen resolves
 *                                            its yellow dot
 */
export function mergeImportRows({ baseList, rows, plates, first = false, isSavedPreparation = () => false }) {
  const nextList = baseList.map((ing) => ({ ...ing }));
  const indexByName = new Map();
  nextList.forEach((ing, idx) => {
    const key = normalizeImportName(ing.name);
    if (key && !indexByName.has(key)) indexByName.set(key, idx);
  });

  let updatedCount = 0;
  let addedCount = 0;
  let keptCount = 0;
  // Saved preparations added onto BECAUSE of the preparation rule — i.e. on a first import
  // that otherwise keeps existing quantities. A repeat import adds onto everything anyway.
  let preparationsAddedOnto = 0;
  const unitConflicts = [];

  // First import never rewrites a quantity the dish already has — unless every one of
  // the source's ingredients is already there, in which case adding onto them is the
  // only thing the import can do (e.g. the first import happened before a reload).
  const existingKeys = new Set(nextList.map((ing) => normalizeImportName(ing.name)).filter(Boolean));
  const keepExisting = first && rows.some((src) => {
    const key = normalizeImportName(src.name);
    return key && !existingKeys.has(key) && Number(src.qty10 || 0) > 0;
  });

  rows.forEach((src) => {
    const key = normalizeImportName(src.name);
    if (!key) return;
    const srcPerPlate = Number(src.qty10 || 0) / 10;
    const addPerPlate = srcPerPlate * plates;
    if (!(addPerPlate > 0)) return;

    const idx = indexByName.get(key);
    if (idx === undefined) {
      nextList.push({
        ...src,
        qty10: Number((addPerPlate * 10).toFixed(4)),
        qty100: 0,
      });
      indexByName.set(key, nextList.length - 1);
      addedCount += 1;
      return;
    }

    const row = nextList[idx];
    const preparation = isSavedPreparation(row.name) === true;
    if (keepExisting && !preparation) {
      keptCount += 1;
      return;
    }

    const merged = addPerPlateOntoRow(row, src.unit, addPerPlate);
    if (!merged) {
      // g onto an ml row (or similar) has no honest conversion — leave the row alone.
      unitConflicts.push(row.name);
      return;
    }
    nextList[idx] = merged;
    updatedCount += 1;
    if (keepExisting && preparation) preparationsAddedOnto += 1;
  });

  return { nextList, updatedCount, addedCount, keptCount, preparationsAddedOnto, unitConflicts };
}
